export interface ColorPreset {
  title: string;
  preset1: {
    light: string;
    subtle: string;
    muted: string;
    dark: string;
    primary: string;
    secondary: string;
  };
  preset2: {
    light: string;
    subtle: string;
    muted: string;
    dark: string;
    primary: string;
    secondary: string;
  };
}

export const colorPresets: ColorPreset[] = [
  {
    title: "Playful",
    preset1: {
      light: "#F9F6F5",
      subtle: "#EFE1DD",
      muted: "#738487",
      dark: "#20393C",
      primary: "#DF529A",
      secondary: "#FFAB19",
    },
    preset2: {
      light: "#FDFCFC",
      subtle: "#FDF0E6",
      muted: "#7A7B9C",
      dark: "#010109",
      primary: "#F08E80",
      secondary: "#90DFFB",
    },
  },
  {
    title: "Bold",
    preset1: {
      light: "#FDFDFC",
      subtle: "#FBF7DA",
      muted: "#867A6E",
      dark: "#000000",
      primary: "#FD7E00",
      secondary: "#FD0004",
    },
    preset2: {
      light: "#FFFFFF",
      subtle: "#F5FDDC",
      muted: "#606990",
      dark: "#0A174C",
      primary: "#E75B86",
      secondary: "#C5FF12",
    },
  },
  {
    title: "Natural",
    preset1: {
      light: "#FCFCFC",
      subtle: "#F0F0E0",
      muted: "#615C4C",
      dark: "#23211A",
      primary: "#698445",
      secondary: "#949F7F",
    },
    preset2: {
      light: "#FCFBF8",
      subtle: "#FFF7E0",
      muted: "#5F735E",
      dark: "#284526",
      primary: "#EFC05D",
      secondary: "#EF8D5D",
    },
  },
  {
    title: "Modern",
    preset1: {
      light: "#FEFCF2",
      subtle: "#D6D1C7",
      muted: "#756E61",
      dark: "#252422",
      primary: "#F54500",
      secondary: "#EF8257",
    },
    preset2: {
      light: "#FCFAF3",
      subtle: "#F6F1DE",
      muted: "#3E3F5B",
      dark: "#15151E",
      primary: "#207F43",
      secondary: "#74BF90",
    },
  },
  {
    title: "Professional",
    preset1: {
      light: "#FBFBFB",
      subtle: "#D0E2F3",
      muted: "#426685",
      dark: "#0C1013",
      primary: "#1469AB",
      secondary: "#F1E562",
    },
    preset2: {
      light: "#FAFAFA",
      subtle: "#E5E5E5",
      muted: "#8B7E68",
      dark: "#000000",
      primary: "#F26541",
      secondary: "#057ECE",
    },
  },
  {
    title: "Elegant",
    preset1: {
      light: "#F9F8F6",
      subtle: "#DFD9E2",
      muted: "#64556D",
      dark: "#2F2833",
      primary: "#C9546B",
      secondary: "#F8E5B3",
    },
    preset2: {
      light: "#FFFBF2",
      subtle: "#FFF1D1",
      muted: "#6B5858",
      dark: "#210E00",
      primary: "#AB3536",
      secondary: "#7A4473",
    },
  },
  {
    title: "Futuristic",
    preset1: {
      light: "#FFFFFF",
      subtle: "#E0E5EB",
      muted: "#5B7B79",
      dark: "#0C0F13",
      primary: "#66FCF2",
      secondary: "#FF4FE8",
    },
    preset2: {
      light: "#FFFFFF",
      subtle: "#F7D4CF",
      muted: "#554B58",
      dark: "#301542",
      primary: "#2F7DF3",
      secondary: "#FF0080",
    },
  },
];

interface TypographyLevel {
  name: string;
  url: string;
  size: number;
  weight: number;
  lineHeight: number;
  letterSpacing: number;
}

export interface FontPreset {
  title: string;
  preset1: {
    h1: TypographyLevel;
    h2: TypographyLevel;
    h3: TypographyLevel;
    h4: TypographyLevel;
    body: TypographyLevel;
  };
  preset2: {
    h1: TypographyLevel;
    h2: TypographyLevel;
    h3: TypographyLevel;
    h4: TypographyLevel;
    body: TypographyLevel;
  };
}

const typographyTemplate: Omit<TypographyLevel, "name"> = {
  url: "",
  size: 16,
  weight: 400,
  lineHeight: 0,
  letterSpacing: 0,
};

const typographyLevelDefaults = {
  h1: { size: 48, weight: 700 },
  h2: { size: 32, weight: 500 },
  h3: { size: 28, weight: 500 },
  h4: { size: 24, weight: 500 },
  body: { size: 16, weight: 400 },
};

interface SimplifiedFontPreset {
  title: string;
  preset1: {
    h1: string;
    h2: string;
    h3: string;
    h4: string;
    body: string;
  };
  preset2: {
    h1: string;
    h2: string;
    h3: string;
    h4: string;
    body: string;
  };
}

function createTypographyLevel(
  name: string,
  level: keyof typeof typographyLevelDefaults,
): TypographyLevel {
  return {
    name,
    url: typographyTemplate.url,
    size: typographyLevelDefaults[level].size,
    weight: typographyLevelDefaults[level].weight,
    lineHeight: typographyTemplate.lineHeight,
    letterSpacing: typographyTemplate.letterSpacing,
  };
}

function generateFontPreset(simplified: SimplifiedFontPreset): FontPreset {
  return {
    title: simplified.title,
    preset1: {
      h1: createTypographyLevel(simplified.preset1.h1, "h1"),
      h2: createTypographyLevel(simplified.preset1.h2, "h2"),
      h3: createTypographyLevel(simplified.preset1.h3, "h3"),
      h4: createTypographyLevel(simplified.preset1.h4, "h4"),
      body: createTypographyLevel(simplified.preset1.body, "body"),
    },
    preset2: {
      h1: createTypographyLevel(simplified.preset2.h1, "h1"),
      h2: createTypographyLevel(simplified.preset2.h2, "h2"),
      h3: createTypographyLevel(simplified.preset2.h3, "h3"),
      h4: createTypographyLevel(simplified.preset2.h4, "h4"),
      body: createTypographyLevel(simplified.preset2.body, "body"),
    },
  };
}

// Simplified preset data - just font names!
const simplifiedFontPresets: SimplifiedFontPreset[] = [
  {
    title: "Playful",
    preset1: {
      h1: "Caprasimo",
      h2: "Caprasimo",
      h3: "Caprasimo",
      h4: "Caprasimo",
      body: "Spline Sans",
    },
    preset2: {
      h1: "Raleway",
      h2: "Raleway",
      h3: "Raleway",
      h4: "Raleway",
      body: "Montserrat",
    },
  },
  {
    title: "Bold",
    preset1: {
      h1: "Impact",
      h2: "Impact",
      h3: "Impact",
      h4: "Impact",
      body: "Archivo",
    },
    preset2: {
      h1: "Bebas Neue",
      h2: "Bebas Neue",
      h3: "Bebas Neue",
      h4: "Bebas Neue",
      body: "Mulish",
    },
  },
  {
    title: "Natural",
    preset1: {
      h1: "IBM Plex Sans",
      h2: "IBM Plex Sans",
      h3: "IBM Plex Sans",
      h4: "IBM Plex Sans",
      body: "Source Sans 3",
    },
    preset2: {
      h1: "Afacad",
      h2: "Afacad",
      h3: "Afacad",
      h4: "Afacad",
      body: "Inter",
    },
  },
  {
    title: "Modern",
    preset1: {
      h1: "Helvetica",
      h2: "Helvetica",
      h3: "Helvetica",
      h4: "Helvetica",
      body: "Arial",
    },
    preset2: {
      h1: "Lexend",
      h2: "Lexend",
      h3: "Lexend",
      h4: "Lexend",
      body: "Instrument Sans",
    },
  },
  {
    title: "Professional",
    preset1: {
      h1: "IBM Plex Sans",
      h2: "IBM Plex Sans",
      h3: "IBM Plex Sans",
      h4: "IBM Plex Sans",
      body: "Roboto",
    },
    preset2: {
      h1: "DM Serif Display",
      h2: "DM Serif Display",
      h3: "DM Serif Display",
      h4: "DM Serif Display",
      body: "Merriweather Sans",
    },
  },
  {
    title: "Elegant",
    preset1: {
      h1: "Yeseva One",
      h2: "Yeseva One",
      h3: "Yeseva One",
      h4: "Yeseva One",
      body: "Montserrat",
    },
    preset2: {
      h1: "Marcellus",
      h2: "Marcellus",
      h3: "Marcellus",
      h4: "Marcellus",
      body: "Raleway",
    },
  },
  {
    title: "Futuristic",
    preset1: {
      h1: "Orbitron",
      h2: "Orbitron",
      h3: "Orbitron",
      h4: "Orbitron",
      body: "Roboto Mono",
    },
    preset2: {
      h1: "Exo 2",
      h2: "Exo 2",
      h3: "Exo 2",
      h4: "Exo 2",
      body: "Michroma",
    },
  },
];

// Generate the full presets from simplified data
export const fontPresets: FontPreset[] =
  simplifiedFontPresets.map(generateFontPreset);

// Default Typography Settings
export const defaultTypography = {
  h1: createTypographyLevel("Aeonik", "h1"),
  h2: createTypographyLevel("Aeonik", "h2"),
  h3: createTypographyLevel("Aeonik", "h3"),
  h4: createTypographyLevel("Aeonik", "h4"),
  body: createTypographyLevel("Inter", "body"),
};
